import { useForm, type SubmitHandler } from 'react-hook-form';
import ResizeTextarea from 'react-textarea-autosize';
import { Box, HStack, IconButton, Textarea } from '@chakra-ui/react';
import { SendHorizonal } from 'lucide-react';
import { useCallback, useContext, useState } from 'react';
import { useTypingStatus } from './hooks';
import { CHAT_MESSAGE_MAX_LENGTH } from './config';
import { ChatContext } from './Context';

type Inputs = {
  message: string;
};

export function ChatInput({
  chatId,
  currentUserDrn,
  onMessagePost,
  InputHeader,
}: {
  chatId: string;
  currentUserDrn: string;
  onMessagePost?: () => void;
  InputHeader?: React.ComponentType<{ chatId: string }>;
}) {
  const ctx = useContext(ChatContext);
  const { startTyping, stopTyping, resetTyping } = useTypingStatus({
    sendTyping: ctx.sendTyping,
    chatId,
    currentUserDrn,
  });

  const [enterToPostMessage, setEnterToPostMessage] = useState(true);

  const { register, handleSubmit, setValue } = useForm<Inputs>({});

  const onSubmit: SubmitHandler<Inputs> = useCallback(
    async (data) => {
      try {
        const message = data.message;

        // don't send empty messages
        if (!message || message.length === 0 || message.trim().length === 0) {
          return;
        }

        resetTyping();

        setValue('message', '');

        await ctx.sendMessage({
          chatId,
          message,
        });

        onMessagePost && onMessagePost();
      } catch (err) {
        console.error(err);
      }
    },
    [ctx, chatId, onMessagePost, resetTyping, setValue],
  );

  const onKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Shift') {
        setEnterToPostMessage(false);
      }

      if (e.key === 'Enter' && enterToPostMessage) {
        // prevent a newline being added
        e.preventDefault();
        // then submit the form
        handleSubmit(onSubmit)();
        return;
      }

      startTyping();
    },
    [enterToPostMessage],
  );

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Shift') {
        setEnterToPostMessage(true);
      }
    },
    [setEnterToPostMessage],
  );

  const onBlur = useCallback(() => {
    setEnterToPostMessage(true);

    stopTyping();
  }, [stopTyping, setEnterToPostMessage]);

  return (
    <Box
      bg="bg.subtle"
      borderTop="1px solid"
      borderColor={'border.default'}
      as="form"
      alignSelf="stretch"
      flexShrink={0}
      onSubmit={handleSubmit(onSubmit)}
    >
      {InputHeader && <InputHeader chatId={chatId} />}
      <HStack p="2" width="100%" alignItems="center">
        <Textarea
          as={ResizeTextarea}
          minH="unset"
          overflow="hidden"
          w="100%"
          resize="none"
          minRows={1}
          maxRows={3}
          placeholder="Send message…"
          autoFocus
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          maxLength={CHAT_MESSAGE_MAX_LENGTH}
          {...register('message', {
            required: true,
            onBlur,
          })}
        />
        <IconButton
          aria-label="support-send-message-button"
          variant="tertiary"
          onClick={handleSubmit(onSubmit)}
          size="sm"
        >
          <SendHorizonal strokeWidth={1.5} />
        </IconButton>
      </HStack>
    </Box>
  );
}
